import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Users, Phone, Mail, MessageSquare, User, CheckCircle2, ArrowLeft, Percent, AlertTriangle, PawPrint } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import TableMap from "../components/TableMap";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { it as itLocale, enUS, de as deLocale, fr as frLocale, es as esLocale } from "date-fns/locale";

const DATE_LOCALES = { it: itLocale, en: enUS, de: deLocale, fr: frLocale, es: esLocale };

export default function Book() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const dateLocale = DATE_LOCALES[i18n.resolvedLanguage] || itLocale;
    const [searchParams] = useSearchParams();
    // Guest mode: arrived from a Private Mirror link → no Google login required
    const mirrorToken = searchParams.get("from") === "mirror" ? searchParams.get("t") : null;
    const isGuest = useMemo(() => !!mirrorToken, [mirrorToken]);

    const [restaurant, setRestaurant] = useState(null);
    const [date, setDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [customerPoints, setCustomerPoints] = useState(0);
    const [selectedSlotId, setSelectedSlotId] = useState("");
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [partySize, setPartySize] = useState(2);
    const [form, setForm] = useState({
        customer_name: user?.name || "",
        customer_phone: "",
        customer_email: user?.email || "",
        special_requests: "",
        allergies: "",
        bringing_pet: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);

    useEffect(() => {
        api.get(`/r/${slug}`).then((r) => setRestaurant(r.data)).catch(() => navigate(isGuest ? `/m/${mirrorToken}` : "/"));
        api.get(`/r/${slug}/slots`).then((r) => setSlots(r.data)).catch(() => {});
        api.get(`/r/${slug}/discounts`).then((r) => setDiscounts(r.data)).catch(() => {});
    }, [slug, navigate]);

    useEffect(() => {
        if (user) {
            api.get("/me/points").then((r) => setCustomerPoints(r.data.points || 0)).catch(() => setCustomerPoints(0));
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            setForm((f) => ({
                ...f,
                customer_name: f.customer_name || user.name,
                customer_email: f.customer_email || user.email,
            }));
        }
    }, [user]);

    useEffect(() => {
        const load = async () => {
            if (!date || !selectedSlotId) { setTables([]); return; }
            try {
                const d = format(date, "yyyy-MM-dd");
                const r = await api.get(`/r/${slug}/availability`, { params: { date: d, slot_id: selectedSlotId } });
                setTables(r.data);
                setSelectedTable(null);
            } catch { toast.error(t("book.load_tables_error")); }
        };
        load();
    }, [date, selectedSlotId, slug]);

    // Active discount for selected date+slot (filtered by customer points)
    const activeDiscount = (() => {
        if (!date || !selectedSlotId) return null;
        const weekday = (date.getDay() + 6) % 7;
        const matches = discounts.filter((d) => {
            if (!d.active) return false;
            if (d.days_of_week.length && !d.days_of_week.includes(weekday)) return false;
            if (d.slot_ids.length && !d.slot_ids.includes(selectedSlotId)) return false;
            if ((d.min_customer_points ?? 0) > customerPoints) return false;
            return true;
        });
        if (!matches.length) return null;
        return matches.reduce((a, b) => (a.percentage > b.percentage ? a : b));
    })();

    // Max table capacity at the selected slot — used to detect "oversized"
    // parties that don't fit any single table. Only meaningful once tables
    // have been loaded for the chosen date+slot.
    const maxSeats = useMemo(
        () => (tables.length ? Math.max(...tables.map((tt) => tt.seats || 0)) : 0),
        [tables],
    );
    const oversized = tables.length > 0 && partySize > maxSeats;

    // Discounts locked by points (for user motivation)
    const lockedDiscount = (() => {
        if (!date || !selectedSlotId) return null;
        const weekday = (date.getDay() + 6) % 7;
        const locked = discounts.filter((d) => {
            if (!d.active) return false;
            if (d.days_of_week.length && !d.days_of_week.includes(weekday)) return false;
            if (d.slot_ids.length && !d.slot_ids.includes(selectedSlotId)) return false;
            return (d.min_customer_points ?? 0) > customerPoints;
        });
        if (!locked.length) return null;
        return locked.reduce((a, b) => (a.percentage > b.percentage ? a : b));
    })();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!date || !selectedSlotId) {
            toast.error(t("book.select_date_time_table_error"));
            return;
        }
        // Mirror flow: oversized parties are blocked entirely (must phone the restaurant)
        if (oversized && isGuest) {
            toast.error(t("book.oversized_mirror_blocked"));
            return;
        }
        // Standard flow oversized: allow submission with no table — restaurateur assigns later
        if (!oversized && !selectedTable) {
            toast.error(t("book.select_date_time_table_error"));
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                date: format(date, "yyyy-MM-dd"),
                slot_id: selectedSlotId,
                table_id: oversized ? null : selectedTable.table_id,
                party_size: parseInt(partySize, 10),
                ...form,
            };
            const r = await api.post(
                isGuest ? `/m/${mirrorToken}/reservations?slug=${slug}` : `/r/${slug}/reservations`,
                payload,
            );
            setConfirmed(r.data);
            toast.success(t("book.created_toast"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("book.create_error_fallback"));
        } finally {
            setSubmitting(false);
        }
    };

    if (confirmed) {
        return (
            <div className="min-h-screen bg-ink pt-32 pb-20 px-6 grain" data-testid="booking-confirmed">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-ink-surface border border-gold/30 rounded-sm p-10 text-center">
                        <CheckCircle2 size={64} className="text-gold mx-auto mb-6" strokeWidth={1} />
                        <h1 className="font-serif text-4xl text-white mb-2">{t("book.confirmed_title")}</h1>
                        <p className="text-gold text-sm tracking-widest uppercase mb-6">{confirmed.restaurant_name}</p>
                        <p className="text-gray-400 mb-8">{isGuest ? t("book.confirmed_no_email_hint") : t("book.confirmed_email_hint")}</p>
                        <div className="bg-ink rounded-sm p-6 text-left space-y-3 text-sm border border-white/5">
                            <Row label={t("book.row_customer")} value={confirmed.customer_name} />
                            <Row label={t("book.row_date")} value={confirmed.date} />
                            <Row label={t("book.row_time")} value={`${confirmed.slot_time} — ${confirmed.slot_label}`} />
                            <Row label={t("book.row_table")} value={confirmed.table_number ? t("book.row_table_value", { number: confirmed.table_number, seats: confirmed.table_seats }) : t("book.row_table_pending")} />
                            <Row label={t("book.row_party")} value={confirmed.party_size} />
                            {confirmed.discount_percentage > 0 && (
                                <Row label={t("book.row_discount")} value={<span className="text-gold">{confirmed.discount_name} · −{confirmed.discount_percentage}%</span>} />
                            )}
                            <Row label={t("book.row_status")} value={<span className={`uppercase text-xs tracking-widest ${confirmed.status === "confirmed" ? "text-emerald-400" : "text-gold"}`}>{confirmed.status === "confirmed" ? t("book.status_confirmed") : t("book.status_pending")}</span>} />
                        </div>
                        <div className="flex gap-3 mt-8 justify-center flex-wrap">
                            {isGuest ? (
                                <button onClick={() => navigate(`/m/${mirrorToken}`)} className="bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light" data-testid="confirmed-back-mirror-btn">
                                    {t("book.back_to_mirror_page")}
                                </button>
                            ) : (
                                <button onClick={() => navigate("/my-reservations")} className="bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light" data-testid="confirmed-view-reservations-btn">
                                    {t("book.my_reservations")}
                                </button>
                            )}
                            <button onClick={() => { setConfirmed(null); setSelectedTable(null); }} className="border border-white/20 text-white px-6 py-3 rounded-sm hover:border-gold hover:text-gold" data-testid="confirmed-new-btn">
                                {t("book.new_booking")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink pt-32 pb-20 px-6 grain" data-testid="book-page">
            <div className="max-w-7xl mx-auto">
                <div className="mb-12">
                    <Link to={isGuest ? `/m/${mirrorToken}` : `/r/${slug}`} className="inline-flex items-center gap-2 text-gray-400 hover:text-gold text-xs uppercase tracking-widest mb-6" data-testid="back-to-restaurant">
                        <ArrowLeft size={14} /> {restaurant?.name || t("book.back_to_restaurant_label")}
                    </Link>
                    <h1 className="font-serif text-5xl lg:text-6xl text-white leading-tight">
                        {t("book.title")} <span className="italic text-gold">{restaurant?.name}</span>
                    </h1>
                    {isGuest && (
                        <p className="text-xs text-emerald-300/80 mt-3" data-testid="guest-mode-banner">
                            {t("book.guest_mode_banner")}
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5 space-y-6">
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-6">
                            <Label icon={CalendarIcon} text={t("book.select_date")} />
                            <div className="mt-4 flex justify-center">
                                <Calendar mode="single" selected={date} onSelect={setDate} locale={dateLocale}
                                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                    className="bg-transparent" data-testid="booking-calendar" />
                            </div>
                        </div>

                        <div className="bg-ink-surface border border-white/10 rounded-sm p-6">
                            <Label icon={CalendarIcon} text={t("book.time_slot")} />
                            <div className="mt-4 grid grid-cols-3 gap-2">
                                {slots.map((s) => (
                                    <button type="button" key={s.slot_id}
                                        onClick={() => setSelectedSlotId(s.slot_id)}
                                        data-testid={`slot-${s.time.replace(":", "")}`}
                                        className={`px-3 py-2.5 border rounded-sm text-sm transition-all ${selectedSlotId === s.slot_id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-gray-300 hover:border-gold/50 hover:text-white"}`}>
                                        <div className="font-semibold">{s.time}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{t(`service.${s.service}`, { defaultValue: s.service })}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="bg-ink-surface border border-white/10 rounded-sm p-6">
                            <Label icon={Users} text={t("book.party_size")} />
                            <div className="mt-4 flex items-center gap-4">
                                <button type="button" onClick={() => setPartySize(Math.max(1, partySize - 1))}
                                    className="w-12 h-12 border border-white/20 rounded-sm text-white hover:border-gold hover:text-gold text-xl"
                                    data-testid="party-decrement">−</button>
                                <div className="flex-1 text-center font-serif text-4xl text-gold" data-testid="party-size-display">{partySize}</div>
                                <button type="button" onClick={() => setPartySize(Math.min(20, partySize + 1))}
                                    className="w-12 h-12 border border-white/20 rounded-sm text-white hover:border-gold hover:text-gold text-xl"
                                    data-testid="party-increment">+</button>
                            </div>
                        </div>

                        <div className="bg-ink-surface border border-white/10 rounded-sm p-6 space-y-4">
                            <Label icon={User} text={t("book.your_data")} />
                            <InputField icon={User} placeholder={t("book.name_placeholder")} value={form.customer_name}
                                onChange={(v) => setForm({ ...form, customer_name: v })} required testid="form-name" />
                            <InputField icon={Phone} placeholder={t("book.phone_placeholder")} value={form.customer_phone}
                                onChange={(v) => setForm({ ...form, customer_phone: v })} type="tel" required testid="form-phone" />
                            <InputField icon={Mail} placeholder={t("book.email_placeholder")} value={form.customer_email}
                                onChange={(v) => setForm({ ...form, customer_email: v })} type="email" required testid="form-email" />
                            <div className="relative">
                                <MessageSquare className="absolute left-3 top-3 text-gray-500" size={16} strokeWidth={1.5} />
                                <textarea value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                                    placeholder={t("book.special_requests_placeholder")} rows={3} data-testid="form-notes"
                                    className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm resize-none" />
                            </div>

                            <div className="relative">
                                <AlertTriangle className="absolute left-3 top-3 text-rose-400/80" size={16} strokeWidth={1.5} />
                                <textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                                    placeholder={t("book.allergies_placeholder")} rows={2} maxLength={500} data-testid="form-allergies"
                                    className="w-full pl-10 pr-3 py-3 bg-transparent border border-rose-400/20 rounded-sm text-white placeholder-gray-500 focus:border-rose-400 focus:outline-none focus:ring-1 focus:ring-rose-400 text-sm resize-none" />
                            </div>

                            <div className={`flex items-center justify-between gap-3 border rounded-sm px-4 py-3 ${restaurant?.pets_allowed ? "border-emerald-400/30 bg-emerald-400/5" : "border-white/10 bg-white/5 opacity-60"}`} data-testid="pets-section">
                                <div className="flex items-center gap-3">
                                    <PawPrint size={18} className={restaurant?.pets_allowed ? "text-emerald-300" : "text-gray-500"} strokeWidth={1.5} />
                                    <div>
                                        <div className="text-sm text-white font-semibold">{t("book.pets_section_title")}</div>
                                        <div className="text-[11px] uppercase tracking-widest mt-0.5" data-testid="pets-status">
                                            {restaurant?.pets_allowed
                                                ? <span className="text-emerald-300">{t("book.pets_accepted")}</span>
                                                : <span className="text-gray-500">{t("book.pets_not_accepted")}</span>}
                                        </div>
                                    </div>
                                </div>
                                <label className={`inline-flex items-center gap-2 text-xs ${restaurant?.pets_allowed ? "cursor-pointer" : "cursor-not-allowed"}`}>
                                    <input
                                        type="checkbox"
                                        disabled={!restaurant?.pets_allowed}
                                        checked={!!form.bringing_pet}
                                        onChange={(e) => setForm({ ...form, bringing_pet: e.target.checked })}
                                        data-testid="form-bringing-pet"
                                        className="w-4 h-4 accent-emerald-400"
                                    />
                                    <span className={restaurant?.pets_allowed ? "text-white" : "text-gray-500"}>{t("book.bringing_my_pet")}</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 space-y-6">
                        {activeDiscount && (
                            <div className="bg-gold/10 border border-gold/40 rounded-sm p-5 flex items-center gap-4 animate-fade-up" data-testid="discount-banner">
                                <Percent size={28} className="text-gold flex-shrink-0" strokeWidth={1.5} />
                                <div>
                                    <div className="font-serif text-xl text-white">{activeDiscount.name}</div>
                                    <div className="text-gold text-sm">{t("book.discount_unlocked_caption", { pct: activeDiscount.percentage })}</div>
                                    {(activeDiscount.min_customer_points ?? 0) > 0 && (
                                        <div className="text-[10px] uppercase tracking-widest text-gold/80 mt-1">
                                            {t("book.discount_unlocked_by_points", { points: customerPoints })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {lockedDiscount && (
                            <div className="bg-ink-surface border border-dashed border-gold/30 rounded-sm p-4 flex items-center gap-3" data-testid="locked-discount-banner">
                                <Percent size={20} className="text-gold/60 flex-shrink-0" strokeWidth={1.5} />
                                <div className="text-xs text-gray-300">
                                    {t("book.discount_locked_full", {
                                        pct: lockedDiscount.percentage,
                                        name: lockedDiscount.name,
                                        required: lockedDiscount.min_customer_points,
                                        current: customerPoints,
                                        missing: lockedDiscount.min_customer_points - customerPoints,
                                    })}
                                </div>
                            </div>
                        )}

                        {oversized && isGuest && (
                            <div className="bg-rose-500/10 border border-rose-500/40 rounded-sm p-5 flex items-start gap-3" data-testid="oversized-mirror-banner">
                                <AlertTriangle size={22} className="text-rose-300 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                <div className="text-sm text-rose-100 leading-relaxed">
                                    {t("book.oversized_mirror_blocked")}
                                </div>
                            </div>
                        )}
                        {oversized && !isGuest && (
                            <div className="bg-amber-500/10 border border-amber-500/40 rounded-sm p-5 flex items-start gap-3" data-testid="oversized-standard-banner">
                                <AlertTriangle size={22} className="text-amber-300 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                <div className="text-sm text-amber-100 leading-relaxed">
                                    {t("book.oversized_standard_notice")}
                                </div>
                            </div>
                        )}

                        {!oversized && (
                            <TableMap tables={tables} selectedTableId={selectedTable?.table_id} onSelect={setSelectedTable} partySize={partySize} />
                        )}

                        {selectedTable && !oversized && (
                            <div className="bg-gold/10 border border-gold/40 rounded-sm p-6 animate-fade-up">
                                <div className="text-xs uppercase tracking-[0.3em] text-gold">{t("book.table_selected_label")}</div>
                                <div className="font-serif text-2xl text-white mt-2">
                                    {t("book.table_selected_summary", {
                                        number: selectedTable.number,
                                        seats: selectedTable.seats,
                                        location: selectedTable.location === "outdoor" ? t("book.location_outdoor") : t("book.location_indoor"),
                                    })}
                                </div>
                            </div>
                        )}

                        <button type="submit"
                            disabled={
                                submitting ||
                                !date ||
                                !selectedSlotId ||
                                (oversized && isGuest) ||
                                (!oversized && !selectedTable)
                            }
                            data-testid="book-submit-btn"
                            className="w-full bg-gold text-black font-semibold py-5 rounded-sm hover:bg-gold-light disabled:opacity-40 disabled:cursor-not-allowed tracking-widest uppercase text-sm">
                            {submitting ? t("book.submitting") : (oversized && !isGuest ? t("book.confirm_pending_booking") : t("book.confirm_booking"))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Label({ icon: Icon, text }) {
    return <div className="flex items-center gap-2"><Icon size={14} className="text-gold" strokeWidth={1.5} /><span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{text}</span></div>;
}
function InputField({ icon: Icon, placeholder, value, onChange, type = "text", required, testid }) {
    return (
        <div className="relative">
            <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} strokeWidth={1.5} />
            <input type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
                required={required} data-testid={testid}
                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm" />
        </div>
    );
}
function Row({ label, value }) {
    return (
        <div className="flex justify-between items-center pb-2 border-b border-white/5 last:border-0">
            <span className="text-gray-500 text-xs uppercase tracking-widest">{label}</span>
            <span className="text-white">{value}</span>
        </div>
    );
}
